import { computed, getCurrentInstance, inject, provide, type ComputedRef, type Ref } from 'vue';

/**
 * Injection key for the anchor field ids of the form on the canvas. The rules stage keeps them in
 * the shared rules store, but a read-only screen must not write to that singleton: the only
 * exported way to suspend its persistence erases the rules draft of whoever is building the form.
 * So a read-only screen hands the ids down instead, and the canvas provides them to every element
 * below it, table cells included.
 */
export const FORM_BUILDER_ANCHOR_FIELD_IDS_KEY = 'formBuilderAnchorFieldIds';

/** Keeps only the string ids, so a malformed rules structure marks nothing instead of throwing. */
export function normalizeAnchorFieldIds(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((id): id is string => typeof id === 'string') : [];
}

/**
 * Provides the anchor field ids to every element rendered below the canvas. An absent list means
 * nobody handed any, and the builder behaviour of today stays untouched.
 */
export function provideFormBuilderAnchorFieldIds(getIds: () => string[] | undefined): void {
  provide(
    FORM_BUILDER_ANCHOR_FIELD_IDS_KEY,
    computed(() => {
      const ids = getIds();
      return ids === undefined ? null : normalizeAnchorFieldIds(ids);
    })
  );
}

/**
 * Reads the anchor field ids handed down by the screen. `null` means no provider, or no component
 * instance at all: the caller then falls back to the rules store, which is what the builder does.
 */
export function useFormBuilderAnchorFieldIds(): ComputedRef<string[] | null> {
  const anchorFieldIds = getCurrentInstance()
    ? inject<Ref<string[] | null> | null>(FORM_BUILDER_ANCHOR_FIELD_IDS_KEY, null)
    : null;

  return computed(() => anchorFieldIds?.value ?? null);
}
